import {
  ApiError,
  AuthContextType,
  AuthProviderProps,
  LoginData,
  TokenResponse,
  UserData,
} from "./types";
import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";

import config from "../../config";

// Adjust the import path as needed

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const apiUrl = config.apiUrl;

// Single shared axios instance. useAuth() re-exports this same object so
// that the Authorization header set below (on login/refresh/logout) is
// visible to every caller, not just components that read AuthContext directly.
export const api: AxiosInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  // Ensure Django CSRF is satisfied: axios will read the csrftoken cookie and send X-CSRFToken
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<UserData | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // When true, the app will not attempt silent refresh. Used to ensure logout "sticks" in PWAs
  const wasManuallyLoggedOut = (): boolean => {
    try {
      return localStorage.getItem("auth:manualLogout") === "1";
    } catch {
      return false;
    }
  };

  const setManualLogoutFlag = (value: boolean): void => {
    try {
      if (value) {
        localStorage.setItem("auth:manualLogout", "1");
      } else {
        localStorage.removeItem("auth:manualLogout");
      }
    } catch {
      // ignore storage errors
    }
  };

  const updateAxiosHeader = useCallback((token: string | null): void => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, []);

  const logout = useCallback((): void => {
    setManualLogoutFlag(true);
    setAccessToken(null);
    setUser(null);
    // Fire-and-forget server-side logout to clear cookie/blacklist if possible
    try {
      void api.post("/api/logout/");
    } catch {
      // ignore
    }
    updateAxiosHeader(null);
  }, [updateAxiosHeader]);

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    if (wasManuallyLoggedOut()) {
      // Respect manual logout across reloads/PWA contexts
      throw new Error("Manual logout active");
    }
    try {
      const response = await api.post<TokenResponse>("/api/token/refresh/", {});
      const newAccessToken = response.data.access;
      setAccessToken(newAccessToken);
      updateAxiosHeader(newAccessToken);
      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout();
      throw error;
    }
  }, [updateAxiosHeader, logout]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error: ApiError) => {
        type RetryableRequest = InternalAxiosRequestConfig & {
          _retry?: boolean;
        };
        const originalRequest = error.config as RetryableRequest;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (wasManuallyLoggedOut()) {
            // Do not attempt refresh when user explicitly logged out
            return Promise.reject(error);
          }
          originalRequest._retry = true;

          try {
            const newAccessToken = await refreshAccessToken();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      if (wasManuallyLoggedOut()) {
        // Skip auto-refresh when user has explicitly logged out
        updateAxiosHeader(null);
        setLoading(false);
        return;
      }
      if (accessToken) {
        updateAxiosHeader(accessToken);
        try {
          const response = await api.get<UserData>("/api/account/");
          setUser(response.data);
        } catch (error) {
          const apiError = error as ApiError;
          if (apiError.response?.status === 401) {
            try {
              await refreshAccessToken();
            } catch (refreshError) {
              logout();
            }
          } else {
            logout();
          }
        }
      } else {
        // Try to obtain a new access token from refresh cookie if present
        try {
          const newAccess = await refreshAccessToken();
          updateAxiosHeader(newAccess);
          const response = await api.get<UserData>("/api/account/");
          setUser(response.data);
        } catch (e) {
          // not logged in
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [accessToken, updateAxiosHeader, refreshAccessToken, logout]);

  const login = useCallback(
    async ({ username, password }: LoginData): Promise<boolean> => {
      try {
        const response = await api.post<TokenResponse>("/api/token/", {
          username,
          password,
        });

        const { access } = response.data;
        // Clear any prior manual logout marker on successful login
        setManualLogoutFlag(false);
        setAccessToken(access);
        updateAxiosHeader(access);

        const userResponse = await api.get<UserData>("/api/account/");
        setUser(userResponse.data);

        return true;
      } catch (error) {
        console.error("Login failed:", error);
        throw error;
      }
    },
    [updateAxiosHeader]
  );

  // Re-fetch the account so profile edits made elsewhere (e.g. Settings)
  // propagate to everything that renders `user`.
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<UserData>("/api/account/");
      setUser(response.data);
    } catch {
      // keep the stale user; a failed refresh is not a logout
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      login,
      logout,
      refreshUser,
      isAuthenticated: !!user,
      isStaff: user?.is_staff || false,
      loading,
      api,
      accessToken,
    }),
    [user, login, logout, refreshUser, loading, accessToken]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
